import * as React from 'react';
import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import OrderOfOperationsCountQuery from '@graphql/queries/OrderOfOperationsCountQuery';
import ProjectByIdQuery from '@graphql/queries/ProjectByIdQuery';
import GetProjectReports from '@graphql/queries/ProjectReports';
import ReportRpGgvarFlavorProfileCount from '@graphql/queries/ReportRpGgvarFlavorProfileCount';
import ReportTextureCount from '@graphql/queries/ReportTextureCount';
import RpNoveltyIntensityCounts, {
  RpNoveltyIntensityCountsResponse,
} from '@graphql/queries/RpNoveltyIntensityCounts';
import RpProductTimeSeriesCounts, {
  RpProductTimeSeriesCountsResponse,
} from '@graphql/queries/RpProductTimeSeriesCounts';
import { KeyboardBackspace as KeyboardBackspaceIcon } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { MapOptions } from 'components/Report/Maps/MapOptions';
import {
  ProductVersion,
  ProductVersionSet,
} from 'components/Report/ProductVersion';
import ReportDownloadIcon from 'components/Report/ReportDownloadIcon';
import { isArchtypeReportId } from 'components/Report/utils';
import { History } from 'history';
import { QaPreviewReportProvider } from 'hooks/QaPreviewReportContext';
import useIsProd from 'hooks/useIsProd';
import useReportSummary from 'hooks/useReportSummary';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import { Link } from 'react-router-dom';
import sessionTokenRoles from 'selectors/sessionTokenRoles';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';
import {
  useCustomerPreferences,
  projectTabOrder,
  getDefaultMapOptions,
  ProjectTabs,
  projectTabNames,
} from 'services/customerPreferences';

import styles from './ProjectSummary.module.css';
import ImpactDirectionByGgVar from './tabs/CategoryInsightsTab';
import ProjectDataDownload from './tabs/ProjectDataDownload';
import ProjectExecutiveSummary from './tabs/ProjectExecutiveSummary';
import ProjectFlavorSummary from './tabs/ProjectFlavorSummary';
import ProjectMapSummary from './tabs/ProjectMapSummary';
import ProjectNorms from './tabs/ProjectNorms';
import ProjectNoveltyAnalysisSummary from './tabs/ProjectNoveltyAnalysisSummary';
import ProjectOptimizationSummary from './tabs/ProjectOptimizationSummary';
import ProjectReportSummary from './tabs/ProjectReportSummary';
import ProjectTextureSummary from './tabs/ProjectTextureSummary';
import TrendAnalysis from './tabs/TrendAnalysis';
import { ProjectSummaryQaPreview } from './types';
import useStyles from './useStyles';
import {
  showCategoryInsights,
  showDataDownload,
  showExecutiveSummary,
  showFlavor,
  showMap,
  showNorms,
  showNoveltyAnalysis,
  showOptimizationSummary,
  showTexture,
  showTrendAnalysis,
  buildProjectReportLists,
  getQaPreviewContext,
  showMapOnly,
} from './utils';
import LoadingScreen from '../../components/LoadingScreen';
import {
  PROJECTS,
  PROJECT_SUMMARY_WITH_REPORT,
} from '../../constants/routePaths';
import { PageHeader } from '../../styles/themeComponents';
import { nvlReportId, checkIsWrongWorkspace } from '../../utils/afsUtils';
import formatPath from '../../utils/formatPath';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;
  return <div>{value === index && children}</div>;
};

interface Props extends RouterProps {
  match: Record<string, any>;
  location: Record<string, any>;
  projectId?: number;
  workspaceId?: number;
  qaPreview?: ProjectSummaryQaPreview;
}

interface TabConfig {
  id: number;
  label: string;
  component: React.ReactNode;
  showCondition: () => boolean;
}

const ProjectSummary: React.FC<Props> = (props) => {
  const { match, history, location, qaPreview } = props;
  const {
    isQaPreview,
    isStandalonePreview,
    previewReportId,
    previewProjectId,
  } = getQaPreviewContext(qaPreview);
  const projectId = previewProjectId ?? match.params.projectId;

  const { t } = useTranslation();
  const isProd = useIsProd();

  const theme = useTheme();
  const classes = useStyles(theme);

  const activeWorkspaceId = useSelector((state) =>
    selectWorkspaceProducerId(state),
  );
  const workspaceId = qaPreview?.workspaceId ?? activeWorkspaceId;
  const userRoles = useSelector((state) => sessionTokenRoles(state));

  const { data: projectCheck, loading: projectCheckLoading } = useQuery<{
    project: { id: number; workspaceId: number } | null;
  }>(ProjectByIdQuery, {
    variables: { projectId: parseInt(projectId) },
    skip: isQaPreview,
  });

  // QA reviewers can preview a report inside any workspace's project
  const isWrongWorkspace =
    !isQaPreview &&
    checkIsWrongWorkspace(parseInt(projectId), projectCheck, workspaceId);

  const { loading, error, data } = useQuery<reports.ProjectReport>(
    GetProjectReports,
    {
      variables: { projectID: parseInt(projectId) },
      fetchPolicy: 'no-cache',
      skip: isWrongWorkspace || projectCheckLoading || isStandalonePreview,
    },
  );
  const [selectedReportId, setSelectedReportId] = useState<string>('');

  const [parentReportId, setParentReportId] = useState<string>('');
  const [selectedReportType, setSelectedReportType] = useState<string>('');
  const [projectReports, setProjectReports] = useState<
    reports.ProjectReportSummary[]
  >([]);
  const [flavorReports, setFlavorReports] = useState<
    reports.ReportProductInfo[]
  >([]);
  const [projectName, setProjectName] = useState<string>('');
  const [value, setValue] = useState(ProjectTabs.EXECUTIVE_SUMMARY);
  const [products, setProducts] = useState<ProductVersionSet>(
    new ProductVersionSet(),
  );
  const [product, setProduct] = useState<ProductVersion>(
    new ProductVersion(null, null),
  );
  const [excludedProducts, setExcludedProducts] = useState<ProductVersionSet>(
    new ProductVersionSet(),
  );

  const customerPreferences = useCustomerPreferences(
    isWrongWorkspace || projectCheckLoading,
    qaPreview?.workspaceId,
  );

  const tabNames = projectTabNames(t);
  const tabOrder = projectTabOrder(customerPreferences);

  const [flavorMapOptions, setFlavorMapOptions] = useState<MapOptions>(
    getDefaultMapOptions(customerPreferences),
  );
  const [textureMapOptions, setTextureMapOptions] = useState<MapOptions>(
    getDefaultMapOptions(customerPreferences),
  );

  const [displayParentReport, setDisplayParentReport] = useState(false);

  const {
    data: reportFlavorCnt,
    loading: reportFlavorLoading,
    error: reportFlavorError,
  } = useQuery<reports.ReportFlavorCountsResponse>(
    ReportRpGgvarFlavorProfileCount,
    {
      variables: {
        reportID: nvlReportId(selectedReportId),
      },
      skip: isWrongWorkspace || projectCheckLoading,
    },
  );

  const { data: reportTextureCnt } =
    useQuery<reports.ReportTextureCountResponse>(ReportTextureCount, {
      variables: {
        reportID: nvlReportId(selectedReportId),
      },
      skip: isWrongWorkspace || projectCheckLoading,
    });

  const { data: optSummaryCnt } =
    useQuery<reports.ReportOrderOfOperationsCountResponse>(
      OrderOfOperationsCountQuery,
      {
        variables: {
          reportID: nvlReportId(selectedReportId),
        },
        skip: isWrongWorkspace || projectCheckLoading,
      },
    );

  const { data: noveltyCnt } = useQuery<RpNoveltyIntensityCountsResponse>(
    RpNoveltyIntensityCounts,
    {
      variables: {
        reportID: nvlReportId(selectedReportId),
      },
      skip: isWrongWorkspace || projectCheckLoading,
    },
  );

  const { data: trendAnalysisCnt } =
    useQuery<RpProductTimeSeriesCountsResponse>(RpProductTimeSeriesCounts, {
      variables: {
        reportID: nvlReportId(selectedReportId),
      },
      skip: isWrongWorkspace || projectCheckLoading,
    });

  const {
    loading: reportSummaryLoading,
    error: reportSummaryError,
    data: reportSummaryData,
  } = useReportSummary(nvlReportId(selectedReportId), {
    skip: isWrongWorkspace || projectCheckLoading,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    qaPreview?.onTabChange?.(newValue);
  };

  const isMapOnly = (reportId: string): boolean => {
    if (isQaPreview && reportId === previewReportId) return qaPreview.mapOnly;
    return !!reportSummaryData && !!showMapOnly(reportId, reportSummaryData);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    browserHistory: History,
  ) => {
    setSelectedReportId(event.target.value);
    setExcludedProducts(new ProductVersionSet());
    if (isQaPreview) return;
    browserHistory.push({
      pathname: formatPath(PROJECT_SUMMARY_WITH_REPORT, {
        projectId: projectId,
        reportId: event.target.value,
      }),
    });
  };

  useEffect(() => {
    const restoreTab = () => {
      if (isQaPreview) {
        // Executive summary is hidden for map-only reports
        const defaultTab = qaPreview.mapOnly
          ? ProjectTabs.PROJECT_SUMMARY
          : tabOrder[0];
        setValue(qaPreview.initialTab ?? defaultTab);
      } else if (location.state) {
        const productId = location?.state?.productId;
        const version = location?.state?.version;

        setValue(location?.state?.tabIdx ?? tabOrder[0]);

        setProduct(
          new ProductVersion(
            productId ? parseInt(productId) : productId,
            version,
          ),
        );
      } else {
        setValue(tabOrder[0]);
      }
    };

    const loadReports = () => {
      const ready =
        !projectCheckLoading && !isWrongWorkspace && !loading && !error;
      if (!ready || !(data?.project || isQaPreview)) return;

      setProjectName(data?.project?.name ?? qaPreview?.report.projectName);
      const { projectReports: reports, flavorReports: reportsForFlavor } =
        buildProjectReportLists(
          data?.project,
          qaPreview?.report,
          ` — ${t('reportsQa.preview.reportSuffix')}`,
        );
      setProjectReports(reports);
      setFlavorReports(reportsForFlavor);

      const requestedReportId = isQaPreview
        ? previewReportId
        : match.params.reportId;
      if (
        requestedReportId &&
        reports.some((r) => r.reportId === requestedReportId)
      ) {
        setSelectedReportId(requestedReportId);
      }
    };

    restoreTab();
    loadReports();
  }, [loading]);

  useEffect(() => {
    setSelectedReportType(
      projectReports.find((r) => r.reportId === selectedReportId)?.reportType,
    );

    setParentReportId(
      projectReports.find(
        (r) =>
          r.jobId ===
          projectReports.find((r) => r.reportId === selectedReportId)
            ?.parentJobId,
      )?.reportId,
    );
  }, [projectReports, selectedReportId]);

  useEffect(() => {
    if (reportSummaryData) {
      const productsArray = reportSummaryData.allReportJobs.nodes
        .flatMap((n) => n.reportSummariesByReportId)
        .filter((rs) => rs != null)
        .flatMap((e) => e.edges)
        .map((n) => n.node)
        .flatMap((r) => new ProductVersion(r.productByProductId.id, r.version));
      const productsSet = new ProductVersionSet();
      productsArray.forEach((p) => productsSet.add(p));
      setProducts(productsSet);
    }
  }, [selectedReportId, reportSummaryLoading]);

  useEffect(() => {
    return () => {
      history.listen(() => {
        window.scrollTo(0, 0);
      });
    };
  }, []);
  // Switch to Project Summary tab if Executive Summary should be hidden (e.g., Map Only reports)
  useEffect(() => {
    if (!reportSummaryData) return;

    const shouldShowExecSummary = showExecutiveSummary(
      isMapOnly(selectedReportId),
    );

    if (!shouldShowExecSummary && value === ProjectTabs.EXECUTIVE_SUMMARY) {
      setValue(ProjectTabs.PROJECT_SUMMARY);
    }
  }, [selectedReportId, reportSummaryData, value, qaPreview?.mapOnly]);

  if (projectCheckLoading || loading || reportSummaryLoading) {
    return <LoadingScreen />;
  }

  if (isWrongWorkspace) {
    return (
      <div>
        <h2>{t('errors.accessForbidden', 'Access Forbidden')}</h2>
        <p>
          {t(
            'errors.noProjectAccess',
            'You do not have access to view this project. Please ensure you are in the correct workspace.',
          )}
        </p>
      </div>
    );
  }

  const showTabs: () => boolean = () => !!selectedReportId;

  const tabConfigs: TabConfig[] = [
    {
      id: ProjectTabs.EXECUTIVE_SUMMARY,
      label: tabNames[ProjectTabs.EXECUTIVE_SUMMARY],
      component: (
        <ProjectExecutiveSummary
          projectId={parseInt(projectId)}
          projectReports={projectReports}
          selectedReportId={selectedReportId}
        />
      ),
      showCondition: () => showExecutiveSummary(isMapOnly(selectedReportId)),
    },
    {
      id: ProjectTabs.PROJECT_SUMMARY,
      label: tabNames[ProjectTabs.PROJECT_SUMMARY],
      component: (
        <ProjectReportSummary
          projectId={parseInt(projectId)}
          projectReports={projectReports}
          selectedReportId={selectedReportId}
          parentReportId={parentReportId}
          excludedProducts={excludedProducts}
          setExcludedProducts={setExcludedProducts}
          flavorMapOptions={flavorMapOptions}
          setFlavorMapOptions={setFlavorMapOptions}
          displayParentReport={displayParentReport}
          setDisplayParentReport={setDisplayParentReport}
          mapOnly={isMapOnly(selectedReportId)}
        />
      ),
      showCondition: () => true,
    },
    {
      id: ProjectTabs.OPTIMIZATION_SUMMARY,
      label: tabNames[ProjectTabs.OPTIMIZATION_SUMMARY],
      component: (
        <ProjectOptimizationSummary
          reportId={selectedReportId}
          projectId={projectId}
        />
      ),
      showCondition: () =>
        showOptimizationSummary(
          isProd,
          isMapOnly(selectedReportId),
          selectedReportType,
          optSummaryCnt,
        ),
    },
    {
      id: ProjectTabs.FLAVOR_SUMMARY,
      label: tabNames[ProjectTabs.FLAVOR_SUMMARY],
      component: (
        <ProjectFlavorSummary
          projectId={parseInt(projectId)}
          projectReports={flavorReports}
          selectedReportId={selectedReportId}
          product={product}
        />
      ),
      showCondition: () =>
        showFlavor(isMapOnly(selectedReportId), reportFlavorCnt),
    },
    {
      id: ProjectTabs.TEXTURE_SUMMARY,
      label: tabNames[ProjectTabs.TEXTURE_SUMMARY],
      component: (
        <ProjectTextureSummary
          projectId={parseInt(projectId)}
          reportId={selectedReportId}
          excludedProducts={excludedProducts}
          parentReportId={parentReportId}
          textureMapOptions={textureMapOptions}
          setTextureMapOptions={setTextureMapOptions}
          displayParentReport={displayParentReport}
          setDisplayParentReport={setDisplayParentReport}
        />
      ),
      showCondition: () =>
        showTexture(userRoles, isMapOnly(selectedReportId), reportTextureCnt),
    },
    {
      id: ProjectTabs.DATA_DOWNLOAD,
      label: tabNames[ProjectTabs.DATA_DOWNLOAD],
      component: (
        <ProjectDataDownload
          projectId={projectId}
          reportId={selectedReportId}
          products={products}
        />
      ),
      showCondition: () =>
        showDataDownload(
          customerPreferences,
          userRoles,
          isMapOnly(selectedReportId),
        ),
    },
    {
      id: ProjectTabs.PROJECT_MAP,
      label: tabNames[ProjectTabs.PROJECT_MAP],
      component: (
        <ProjectMapSummary
          projectId={projectId}
          workspaceId={workspaceId}
          reportId={selectedReportId}
        />
      ),
      showCondition: () =>
        showMap(customerPreferences, isMapOnly(selectedReportId)),
    },
    {
      id: ProjectTabs.NOVELTY_ANALYSIS,
      label: tabNames[ProjectTabs.NOVELTY_ANALYSIS],
      component: (
        <ProjectNoveltyAnalysisSummary
          projectId={projectId}
          workspaceId={workspaceId}
          reportId={selectedReportId}
        />
      ),
      showCondition: () =>
        showNoveltyAnalysis(
          isMapOnly(selectedReportId),
          workspaceId,
          noveltyCnt,
        ),
    },
    {
      id: ProjectTabs.CATEGORY_INSIGHTS,
      label: tabNames[ProjectTabs.CATEGORY_INSIGHTS],
      component: (
        <ImpactDirectionByGgVar
          projectId={projectId}
          reportId={selectedReportId}
        />
      ),
      showCondition: () => showCategoryInsights(isProd),
    },
    {
      id: ProjectTabs.TREND_ANALYSIS,
      label: tabNames[ProjectTabs.TREND_ANALYSIS],
      component: (
        <TrendAnalysis projectId={projectId} reportId={selectedReportId} />
      ),
      showCondition: () =>
        showTrendAnalysis(
          customerPreferences,
          isMapOnly(selectedReportId),
          trendAnalysisCnt,
        ),
    },
    {
      id: ProjectTabs.NORMS,
      label: tabNames[ProjectTabs.NORMS],
      component: <ProjectNorms reportId={selectedReportId} />,
      showCondition: () =>
        showNorms(customerPreferences, userRoles, isMapOnly(selectedReportId)),
    },
  ];

  const visibleTabs = tabOrder
    .map((tabId) => tabConfigs.find((tab) => tab.id === tabId))
    .filter((tab) => tab && tab.showCondition());

  return (
    <QaPreviewReportProvider reportId={previewReportId}>
      <Paper className={styles.container}>
        <Grid container>
          {isQaPreview ? (
            <Grid item xs={12}>
              {qaPreview.header}
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Link to={PROJECTS}>
                <div className={styles.header}>
                  <IconButton
                    onClick={history.goBack}
                    size="small"
                    style={{ marginLeft: -26 }}
                  >
                    <KeyboardBackspaceIcon fontSize="small" />
                    <PageHeader>{t('projects.title')}</PageHeader>
                  </IconButton>
                </div>
              </Link>
            </Grid>
          )}
          <Grid item xs={12}>
            <div className={styles.headerTextContainer}>
              <h6 className={styles.summarytHeader}>
                {t('projects.projectSummary.title')}
              </h6>
              <h4 className={styles.summaryTitle}>{projectName}</h4>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className={styles.selectView}>
              <div className={styles.select}>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="report-id-select-label" shrink>
                    {t('projects.projectSummary.selectedReport')}
                  </InputLabel>
                  <Select
                    variant="standard"
                    id="report-id-select"
                    value={selectedReportId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(e, history)
                    }
                  >
                    {projectReports.map((report) => (
                      <MenuItem key={report.reportId} value={report.reportId}>
                        {report.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {selectedReportId && !isArchtypeReportId(selectedReportId) && (
                <div className={styles.icon}>
                  {!isMapOnly(selectedReportId) && (
                    <ReportDownloadIcon reportId={selectedReportId} />
                  )}
                </div>
              )}
            </div>
          </Grid>
          <Grid item xs={12}>
            {showTabs() && (
              <Box>
                <Box
                  sx={{
                    width: '100%',
                  }}
                >
                  <Tabs
                    value={value}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    TabIndicatorProps={{
                      style: {
                        backgroundColor: theme.palette.secondary.main,
                      },
                    }}
                  >
                    {visibleTabs.map((tab) => (
                      <Tab
                        key={tab.id}
                        className={classes.tab}
                        value={tab.id}
                        label={tab.label}
                      />
                    ))}
                  </Tabs>
                </Box>
                {visibleTabs.map((tab) => (
                  <TabPanel key={tab.id} value={value} index={tab.id}>
                    {tab.component}
                  </TabPanel>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    </QaPreviewReportProvider>
  );
};

export default ProjectSummary;
